/** Customer Markdown, tables and catalog images; evidence is private. */
import { Fragment, type ReactNode } from 'react'
import type { Citation } from '@/types/message'

interface MessageContentProps {
  content: string
  citations?: Citation[]
}

const CITATION_MARKER = /\[(\d{1,3})\]/g

const INLINE = new RegExp(
  [
    '\\[([^\\]]+)\\]\\((https?:\\/\\/[^\\s)]+)\\)', // [label](url)
    '\\*\\*([^*]+)\\*\\*', // **bold**
    '(?<![\\w*])\\*([^*\\n]+)\\*(?![\\w*])', // *italic*
    '`([^`\\n]+)`', // `code`
    '(https?:\\/\\/[^\\s<>()]+)', // bare url
    '\\[(\\d{1,3})\\]', // [n] citation marker
  ].join('|'),
  'g',
)

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = []
  let cursor = 0
  let index = 0

  for (const match of text.matchAll(INLINE)) {
    const at = match.index ?? 0
    if (at > cursor) nodes.push(text.slice(cursor, at))
    const [, linkLabel, linkHref, bold, italic, code, bareUrl, citation] = match
    const key = `${keyPrefix}-i${index++}`

    if (linkHref) {
      nodes.push(
        <a
          key={key}
          href={linkHref}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 hover:opacity-80"
        >
          {linkLabel}
        </a>,
      )
    } else if (bold) {
      nodes.push(<strong key={key}>{bold}</strong>)
    } else if (italic) {
      nodes.push(<em key={key}>{italic}</em>)
    } else if (code) {
      nodes.push(
        <code key={key} className="rounded bg-black/10 px-1 py-0.5 font-mono text-[0.9em]">
          {code}
        </code>,
      )
    } else if (bareUrl) {
      nodes.push(
        <a
          key={key}
          href={bareUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2 break-all hover:opacity-80"
        >
          {bareUrl}
        </a>,
      )
    } else if (citation) {
      // Evidence markers are private and never rendered.
    }
    cursor = at + match[0].length
  }

  if (cursor < text.length) nodes.push(text.slice(cursor))
  return nodes
}

const BULLET = /^\s*[-*•]\s+(.*)$/
const NUMBERED = /^\s*(\d+)[.)]\s+(.*)$/

/** Split GFM cells while preserving escaped pipes and inline code. */
function tableCells(line: string): string[] {
  const text = line.trim().replace(/^\|/, '').replace(/(?<!\\)\|$/, '')
  const cells: string[] = []
  let cell = ''
  let inCode = false
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (ch === '\\' && text[i + 1] === '|') { cell += '|'; i++; continue }
    if (ch === '`') inCode = !inCode
    if (ch === '|' && !inCode) { cells.push(cell.trim()); cell = '' }
    else cell += ch
  }
  cells.push(cell.trim())
  return cells
}

export function MessageContent({ content }: MessageContentProps) {
  if (!content) return null

  // Compatibility for historical replies; evidence stays in Admin Logs.
  content = content.replace(CITATION_MARKER, '').replace(/(?:^|\n)\s*(?:Sources|References|المصادر|Références)\s*:?\s*\n[\s\S]*$/, '')

  const lines = content.split('\n')
  const blocks: ReactNode[] = []
  let listItems: { ordered: boolean; text: string }[] = []

  const flushList = (key: string) => {
    if (!listItems.length) return
    const ordered = listItems[0].ordered
    const items = listItems.map((item, i) => (
      <li key={`${key}-li${i}`}>{renderInline(item.text, `${key}-li${i}`)}</li>
    ))
    blocks.push(
      ordered ? (
        <ol key={key} className="my-1 list-decimal space-y-0.5 pl-5">
          {items}
        </ol>
      ) : (
        <ul key={key} className="my-1 list-disc space-y-0.5 pl-5">
          {items}
        </ul>
      ),
    )
    listItems = []
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const image = line.trim().match(/^!\[([^\]\n]*)\]\((https:\/\/[^\s<>()[\]]+)\)$/)
    if (image) {
      flushList(`list-${i}`)
      blocks.push(<figure key={`image-${i}`} className="my-3 max-w-sm overflow-hidden rounded-xl border border-black/10">
        <img src={image[2]} alt={image[1]} loading="lazy" decoding="async" referrerPolicy="no-referrer"
          className="aspect-square w-full object-cover" onError={event => { event.currentTarget.hidden = true }} />
        <figcaption className="px-3 py-2 text-xs opacity-70">{image[1]}</figcaption>
      </figure>)
      continue
    }
    const header = tableCells(line)
    const separators = i + 1 < lines.length ? tableCells(lines[i + 1]) : []
    if ((header.length > 1 || (header.length === 1 && line.includes('|'))) && separators.length === header.length
      && separators.every(cell => /^:?-{3,}:?$/.test(cell))) {
      flushList(`list-${i}`)
      const key = `table-${i}`
      const rows: string[][] = []
      i++
      while (i + 1 < lines.length && lines[i + 1].includes('|')) {
        const row = tableCells(lines[i + 1])
        if (row.length !== header.length) break
        rows.push(row)
        i++
      }
      const align = (n: number): 'left' | 'center' | 'right' =>
        separators[n].endsWith(':') ? (separators[n].startsWith(':') ? 'center' : 'right') : 'left'
      blocks.push(
        <div key={key} className="my-2 max-w-full overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead><tr>{header.map((cell, n) => (
              <th key={n} scope="col" style={{ textAlign: align(n) }} className="border px-3 py-2 font-semibold">
                {renderInline(cell, `${key}-h${n}`)}
              </th>
            ))}</tr></thead>
            <tbody>{rows.map((row, r) => <tr key={r}>{row.map((cell, n) => (
              <td key={n} style={{ textAlign: align(n) }} className="border px-3 py-2">
                {renderInline(cell, `${key}-r${r}-c${n}`)}
              </td>
            ))}</tr>)}</tbody>
          </table>
        </div>,
      )
      continue
    }
    const bullet = line.match(BULLET)
    const numbered = line.match(NUMBERED)
    if (bullet) {
      if (listItems.length && listItems[0].ordered) flushList(`list-${i}`)
      listItems.push({ ordered: false, text: bullet[1] })
      continue
    }
    if (numbered) {
      if (listItems.length && !listItems[0].ordered) flushList(`list-${i}`)
      listItems.push({ ordered: true, text: numbered[2] })
      continue
    }
    flushList(`list-${i}`)
    if (!line.trim()) {
      blocks.push(<div key={`gap-${i}`} className="h-2" />)
      continue
    }
    blocks.push(
      <p key={`p-${i}`} className="whitespace-pre-wrap">
        {renderInline(line, `p-${i}`)}
      </p>,
    )
  }
  flushList('list-end')

  return <Fragment>{blocks}</Fragment>
}

